import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouteContext } from "context/RouteProvider";
import { environment } from "environment/environment";
import { AuthService } from "services/AuthService";
import {
  getSignedInUserRole,
  isSignedInZuluCustomer,
  saveWooOnboardingSession,
  wooDashboardPath,
  wooSignInPath,
  wooSignUpPath,
} from "services/WooCommerceOnboardingSession";
import { UserTypes } from "enum/UserTypes";
import logo from "../../assets/images/logo.png";

interface PublicIntegration {
  id?: string;
  platform?: string;
  externalStoreId?: string;
  externalStoreDomain?: string | null;
  connectionStatus?: string;
  zuluCustomerId?: string | null;
}

const WooCommerceOnboarding: React.FC = () => {
  const [, setIsProtected] = useContext(RouteContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";
  const session = searchParams.get("session") || "";
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const [linked, setLinked] = useState(false);
  const [wrongAccountNote, setWrongAccountNote] = useState("");
  const [integration, setIntegration] = useState<PublicIntegration | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!session) {
        setError("Missing onboarding session. Connect the store from WooCommerce again.");
        setLoading(false);
        return;
      }
      saveWooOnboardingSession(session);
      try {
        const response = await fetch(
          `${environment.api_url}/api/v1/integrations/woocommerce/session?session=${encodeURIComponent(
            session
          )}&shop=${encodeURIComponent(shop)}`
        );
        const body = await response.json();
        if (!response.ok || body.success === false) {
          window.location.replace(wooDashboardPath(session, shop));
          return;
        }
        const connected =
          body.data && body.data.integration ? body.data.integration : null;
        setIntegration(connected);
        const alreadyLinked = !!(connected && connected.zuluCustomerId);
        setLinked(alreadyLinked);
        const dashboardShop = shop || connected?.externalStoreId || "";
        if (alreadyLinked) {
          window.location.replace(wooDashboardPath(session, dashboardShop));
          return;
        }
        if (!alreadyLinked && isSignedInZuluCustomer()) {
          setLinking(true);
          const otp = await AuthService.sendOnboardingOtp();
          if (!otp.success || !otp.data?.email) {
            setError(otp.error || "Could not send a verification code. Try again.");
          } else {
            sessionStorage.setItem("zuluMerchantOtpEmail", otp.data.email);
            setIsProtected(true);
            navigate("/email-verification", {
              replace: true,
              state: { email: otp.data.email },
            });
            return;
          }
        } else if (!alreadyLinked && getSignedInUserRole()) {
          const role = getSignedInUserRole();
          const roleLabel =
            role === UserTypes.SUPER_ADMIN
              ? "an admin"
              : role === UserTypes.DRIVER
              ? "a driver"
              : "a different Zulu account";
          setWrongAccountNote(
            `You are signed in as ${roleLabel}. Sign in as the store owner to attach this shop.`
          );
        }
      } catch {
        setError(
          "Could not reach the Zulu API. Make sure the API is running on port 3015."
        );
      } finally {
        setLinking(false);
        setLoading(false);
      }
    };
    load();
  }, [navigate, session, shop]);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-7 col-lg-6">
          <div className="text-center mb-4">
            <img src={logo} alt="Zulu Courier" style={{ maxHeight: 48 }} />
          </div>
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h3 className="mb-3">
                {linked ? "Store Connected" : "Connect WooCommerce to Zulu"}
              </h3>
              {loading && <p className="text-muted mb-0">Checking the connection…</p>}
              {linking && (
                <p className="text-muted mb-0">Sending a verification code…</p>
              )}
              {!loading && !linking && error && (
                <div className="alert alert-danger mb-0">{error}</div>
              )}
              {!loading && !linking && !error && linked && (
                <>
                  <div className="alert alert-success">
                    Store connected successfully. Opening the order dashboard…
                  </div>
                  <a
                    className="btn btn-primary"
                    href={wooDashboardPath(session, shop || integration?.externalStoreId || "")}
                  >
                    View order dashboard
                  </a>
                </>
              )}
              {!loading && !linking && !error && !linked && (
                <>
                  {wrongAccountNote && (
                    <div className="alert alert-warning">{wrongAccountNote}</div>
                  )}
                  <p className="text-muted">
                    Create a new Zulu business account, or sign in if this shop
                    owner already has one.
                  </p>
                  <p className="text-muted small mb-3">
                    Shop: {shop || integration?.externalStoreId || "—"}
                  </p>
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate(wooSignUpPath(session))}
                    >
                      New Customer
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => navigate(wooSignInPath(session))}
                    >
                      Existing Customer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WooCommerceOnboarding;
